import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Store as StoreIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useAuth } from '../AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useStores, useCreateStore, useUpdateStore, useDeleteStore, CreateStore, UpdateStore } from '../hooks/useApi';

const StoreManagement: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStore, setEditingStore] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateStore>({ 
    name: '', 
    address: '',
    contact_email: '',
    contact_phone: '',
  });

  // API hooks
  const { data: stores, isLoading, error } = useStores();
  const createMutation = useCreateStore();
  const updateMutation = useUpdateStore();
  const deleteMutation = useDeleteStore();

  // 管理者権限チェック
  const isAdmin = userProfile?.roles?.includes('admin') ?? false;

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          店舗管理機能にアクセスするには管理者権限が必要です。
        </Alert>
      </Container>
    );
  }

  const handleOpenDialog = (storeId?: string) => {
    if (storeId) {
      const store = stores?.find(s => s.id === storeId);
      if (store) {
        setEditingStore(storeId);
        setFormData({ 
          name: store.name, 
          address: store.address || '',
          contact_email: store.contact_email || '',
          contact_phone: store.contact_phone || '',
        });
      }
    } else {
      setEditingStore(null);
      setFormData({ 
        name: '', 
        address: '',
        contact_email: '',
        contact_phone: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStore(null);
    setFormData({ 
      name: '', 
      address: '',
      contact_email: '',
      contact_phone: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingStore) {
        await updateMutation.mutateAsync({ 
          id: editingStore, 
          data: formData as UpdateStore 
        });
        showSuccess('店舗情報を更新しました');
      } else {
        await createMutation.mutateAsync(formData);
        showSuccess('新しい店舗を作成しました');
      }
      handleCloseDialog();
    } catch (error) {
      showError(editingStore ? '店舗の更新に失敗しました' : '店舗の作成に失敗しました');
    }
  };

  const handleDelete = async (storeId: string) => {
    if (!window.confirm('この店舗を削除してもよろしいですか？')) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(storeId);
      showSuccess('店舗を削除しました');
    } catch (error) {
      showError('店舗の削除に失敗しました');
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          店舗情報の取得に失敗しました: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StoreIcon />
          店舗管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          店舗を追加
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>店舗名</TableCell>
                  <TableCell>住所</TableCell>
                  <TableCell>店舗ID</TableCell>
                  <TableCell>作成日時</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stores?.map((store) => (
                  <TableRow key={store.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StoreIcon color="primary" />
                        <Typography variant="subtitle1">{store.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {store.address ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon fontSize="small" />
                          {store.address}
                        </Box>
                      ) : (
                        <Chip label="住所未設定" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {store.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(store.created_at).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(store.id)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(store.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )) || []}
              </TableBody>
            </Table>
          </TableContainer>

          {(!stores || stores.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                店舗が登録されていません
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 店舗作成・編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStore ? '店舗情報の編集' : '新しい店舗の作成'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="店舗名"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              InputProps={{
                startAdornment: <StoreIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <TextField
              fullWidth
              label="住所（任意）"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              InputProps={{
                startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <TextField
              fullWidth
              label="連絡先メールアドレス（任意）"
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              placeholder="store@example.com"
            />
            <TextField
              fullWidth
              label="連絡先電話番号（任意）"
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              placeholder="03-1234-5678"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name.trim() || createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              editingStore ? '更新' : '作成'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StoreManagement;